// S3, the staff pages' shell (root 6.5):
//   [HPG] Feedback  App [v]  Queue  Dashboard*  AI reports  Apps  Admin**   [person v]
// The app picker's first entry is "All apps" (/queue, CT-001); "Queue" always opens All tickets.
// "My requests", "Notifications" and "Sign out" are in the person menu. Below md the bar folds into a
// menu. People who can only report see the brand and the person menu.
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import { Check, ChevronDown, Menu, UserRound } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { lastAppId } from '@/lib/last-app';
import { canSeeAiReports, canSeeDashboard, signOut, staffRoleLabel, useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
  /** Path prefixes that make the item current. */
  match: string[];
}

function useNavItems(): NavItem[] {
  const session = useSession();
  if (!session.isStaff) {
    return [];
  }
  const items: NavItem[] = [{ label: 'Queue', href: '/queue', match: ['/queue', '/apps/', '/tickets/'] }];
  if (canSeeDashboard(session)) {
    items.push({ label: 'Dashboard', href: '/dashboard', match: ['/dashboard'] });
  }
  if (canSeeAiReports(session)) {
    items.push({ label: 'AI reports', href: '/ai-reports', match: ['/ai-reports'] });
  }
  items.push({ label: 'Apps', href: '/apps', match: ['/apps'] });
  if (session.isPlatformAdmin) {
    items.push({ label: 'Admin', href: '/admin', match: ['/admin'] });
  }
  return items;
}

function isCurrent(item: NavItem, pathname: string): boolean {
  if (item.label === 'Apps') {
    return pathname === '/apps' || pathname === '/apps/';
  }
  if (item.label === 'Queue') {
    return pathname === '/queue' || pathname.endsWith('/queue') || pathname.startsWith('/tickets/');
  }
  return item.match.some((m) => pathname.startsWith(m));
}

function NavLink({
  item,
  current,
  className,
  onClick,
}: {
  item: NavItem;
  current: boolean;
  className: string;
  onClick?: () => void;
}) {
  const props = { className, 'aria-current': current ? ('page' as const) : undefined, onClick };
  return (
    <Link to={item.href} {...props}>
      {item.label}
    </Link>
  );
}

/** `currentAppId` null is All apps. */
function AppPicker({ currentAppId }: { currentAppId: string | null }) {
  const session = useSession();
  const current = session.apps.find((a) => a.appId === currentAppId);
  // The same role in every app (poc CT-008): Editor, or Admin.
  const role = staffRoleLabel(session);
  if (session.apps.length === 0) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="min-w-0 max-w-48 shrink px-2 text-on-primary hover:bg-primary-hover"
          data-testid="app-picker"
        >
          <span className="hidden text-on-primary/80 sm:inline">App</span>
          <span className="sr-only sm:hidden">App:</span>
          <span className="truncate">{current?.name ?? 'All apps'}</span>
          <ChevronDown aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <Link to="/queue" aria-current={current ? undefined : 'true'} data-testid="app-picker-all">
            <span className="flex-1 font-medium">All apps</span>
            {current ? null : <Check aria-hidden="true" className="size-4" />}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Your apps</DropdownMenuLabel>
        {session.apps.map((app) => (
          <DropdownMenuItem key={app.appId} asChild>
            <Link
              to="/apps/$appId/queue"
              params={{ appId: app.appId }}
              aria-current={app.appId === current?.appId ? 'true' : undefined}
            >
              <span className="flex-1">{app.name}</span>
              <span className="text-xs text-muted">{role}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PersonMenu() {
  const session = useSession();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="max-w-52 px-2 text-on-primary hover:bg-primary-hover"
          data-testid="person-menu"
        >
          <UserRound aria-hidden="true" />
          <span className="hidden truncate sm:inline">{session.person.displayName}</span>
          <span className="sr-only sm:hidden">{session.person.displayName}, account menu</span>
          <ChevronDown aria-hidden="true" className="hidden sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="text-sm text-text">{session.person.displayName}</span>
          {session.isTestSignIn ? <Badge variant="warning">Test sign-in</Badge> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/my-requests">My requests</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/notifications">Notifications</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const params = useParams({ strict: false }) as { appId?: string };
  // The picker: All apps on /queue, the app in the URL, else the remembered choice (All apps by default).
  const remembered = lastAppId();
  const currentAppId =
    pathname === '/queue'
      ? null
      : (params.appId ?? (remembered && session.apps.some((a) => a.appId === remembered) ? remembered : null));
  const items = useNavItems();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-surface-raised px-3 py-2 text-text focus:not-sr-only focus:absolute focus:top-2 focus:left-2"
      >
        Skip to main content
      </a>
      <header className="bg-primary text-on-primary">
        <div className="mx-auto flex max-w-screen-2xl items-center gap-1 px-2 py-1.5 sm:px-4">
          {items.length > 0 ? (
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-on-primary hover:bg-primary-hover md:hidden">
                  <Menu aria-hidden="true" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetTitle>Feedback</SheetTitle>
                <SheetDescription className="sr-only">Go to a page of the Feedback tool.</SheetDescription>
                <nav aria-label="Main">
                  <ul className="flex flex-col gap-1">
                    {items.map((item) => (
                      <li key={item.label}>
                        <NavLink
                          item={item}
                          current={isCurrent(item, pathname)}
                          onClick={() => setMenuOpen(false)}
                          className="block rounded-md px-3 py-2 hover:bg-surface aria-[current=page]:bg-surface aria-[current=page]:font-semibold"
                        />
                      </li>
                    ))}
                  </ul>
                </nav>
              </SheetContent>
            </Sheet>
          ) : null}
          <Link to="/" className="mr-1 flex shrink-0 items-center gap-2 rounded-md px-1 py-1 font-semibold sm:mr-2">
            <span className="rounded-sm bg-accent px-1.5 py-0.5 text-xs font-bold text-on-accent">HPG</span>
            <span>Feedback</span>
          </Link>
          {session.isStaff ? <AppPicker currentAppId={currentAppId} /> : null}
          <nav aria-label="Main" className="hidden md:block">
            <ul className="flex items-center gap-0.5">
              {items.map((item) => (
                <li key={item.label}>
                  <NavLink
                    item={item}
                    current={isCurrent(item, pathname)}
                    className={cn(
                      'block rounded-md px-3 py-2 text-sm hover:bg-primary-hover',
                      'aria-[current=page]:bg-primary-hover aria-[current=page]:font-semibold',
                    )}
                  />
                </li>
              ))}
            </ul>
          </nav>
          <div className="ml-auto shrink-0">
            <PersonMenu />
          </div>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-6 focus:outline-none">
        {children}
      </main>
    </div>
  );
}
